import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, Mail, CheckCircle2, X, BookOpen, Layers, Camera } from "lucide-react";
import { createSession, checkDomainPolicy, submitAccessRequest } from "../lib/api";
import { saveSession } from "../lib/session";
import PricingBookViewer from "../components/PricingBookViewer";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const EmailGate = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessSubmitting, setAccessSubmitting] = useState(false);
  const [accessSubmitted, setAccessSubmitted] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [accessForm, setAccessForm] = useState({
    full_name: "",
    company: "",
    use_case: "",
  });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(v)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      // First check the domain policy. If not whitelisted → open access form.
      const policy = await checkDomainPolicy(v);
      if (!policy.allowed) {
        setError(
          policy.reason === "invalid_email"
            ? "Please enter a valid email address."
            : `Your domain (${policy.domain || "—"}) isn't whitelisted yet. Click "Request Access" below and we'll set you up.`
        );
        setAccessOpen(true);
        return;
      }
      const session = await createSession(v);
      saveSession(session);
      const label = session.discount_label ? ` (${session.discount_label}, ${session.discount_percent}% off)` : "";
      toast.success(`Welcome aboard${label}. Starting your quote session.`);
      navigate("/chat");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAccessSubmit = async (e) => {
    e.preventDefault();
    if (!accessForm.full_name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      toast.error("Please enter a valid email address above first.");
      return;
    }
    setAccessSubmitting(true);
    try {
      await submitAccessRequest({
        email: email.trim().toLowerCase(),
        full_name: accessForm.full_name.trim(),
        company: accessForm.company.trim() || null,
        use_case: accessForm.use_case.trim() || null,
      });
      setAccessSubmitted(true);
      toast.success("Request sent. We'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send your request. Try again.";
      toast.error(msg);
    } finally {
      setAccessSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      {bookOpen && <PricingBookViewer onClose={() => setBookOpen(false)} />}
      {/* Left: visual panel */}
      <div
        className="relative hidden md:block"
        style={{ background: "#FFFBEB" }}
        data-testid="auth-hero-panel"
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1581508512961-0e3b9524db40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxwcmludGluZyUyMHByZXNzJTIwbW9kZXJufGVufDB8fHx8MTc3NjU1NDMzOHww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "multiply",
          }}
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-10" style={{ color: "var(--ups-ink)" }}>
          <div className="flex items-center gap-3">
            <img
              src="/site-logo.webp"
              alt="Roswell · Your Printhub Partners"
              className="h-14 w-auto rounded-sm"
            />
            <div className="text-xs font-mono" style={{ color: "var(--ups-subtext)" }}>v1.0 · Quote Engine</div>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tight">
              Instant print quotes
              <br />
              <span style={{ color: "#D97706" }}>with your discount.</span>
            </h1>
            <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--ups-subtext)" }}>
              Describe what you&apos;re printing. The bot calculates retail pricing
              and applies your partner discount automatically. Submit the order
              in one click.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                "AI-assisted retail pricing with transparent math",
                "Your pre-negotiated partner discount, applied every time",
                "Attach artwork, auto-email to the print floor",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 size={18} style={{ color: "#D97706" }} className="mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            {/* Portfolio hero — the big "see our work" tile (per user choice 2b, bigger than the other two) */}
            <a
              href="/portfolio"
              className="mt-8 group flex items-stretch gap-0 rounded-sm overflow-hidden transition-all hover:-translate-y-0.5"
              style={{
                background: "#FFB500",
                color: "#1C1917",
                border: "3px solid #1C1917",
                boxShadow: "6px 6px 0 #1C1917",
                minHeight: 88,
              }}
              data-testid="open-portfolio"
            >
              <span
                className="w-20 flex items-center justify-center shrink-0"
                style={{ background: "#1C1917", color: "#FFB500" }}
              >
                <Camera size={32} strokeWidth={2.2} />
              </span>
              <span className="flex-1 px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-left leading-tight">
                  <span className="block text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
                    See our work
                  </span>
                  <span className="block font-display text-2xl sm:text-[26px] font-black leading-[1.05] mt-0.5">
                    Portfolio
                  </span>
                  <span className="block text-[11px] font-mono opacity-75 mt-1">
                    Real photos · sorted by industry · click any to enlarge
                  </span>
                </span>
                <ArrowRight
                  size={22}
                  className="shrink-0 transition-transform group-hover:translate-x-1.5"
                  strokeWidth={2.5}
                />
              </span>
            </a>

            {/* Retail Pricing Book launcher — opens an interactive flipbook viewer */}
            <button
              type="button"
              onClick={() => setBookOpen(true)}
              className="mt-3 group inline-flex items-center gap-3 px-4 py-3 rounded-sm transition-all"
              style={{
                background: "#1C1917",
                color: "#FFFBEB",
                border: "2px solid #1C1917",
                boxShadow: "4px 4px 0 #FFB500",
              }}
              data-testid="open-pricing-book-button"
            >
              <span
                className="w-9 h-9 flex items-center justify-center shrink-0"
                style={{ background: "#FFB500", color: "#1C1917" }}
              >
                <BookOpen size={18} strokeWidth={2.5} />
              </span>
              <span className="text-left leading-tight">
                <span className="block text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "#FFB500" }}>
                  Browse the catalog
                </span>
                <span className="block font-display text-sm font-bold">
                  Print Pricing Book 2026
                </span>
                <span className="block text-[10px] font-mono opacity-70 mt-0.5">
                  Flip through · swipe to turn pages
                </span>
              </span>
              <ArrowRight
                size={16}
                className="ml-2 transition-transform group-hover:translate-x-1"
                style={{ color: "#FFB500" }}
              />
            </button>

            {/* Material Library — public, visual catalog with photos + plain-English copy */}
            <a
              href="/materials"
              className="mt-3 group inline-flex items-center gap-3 px-4 py-3 rounded-sm transition-all"
              style={{
                background: "#FFFBEB",
                color: "#1C1917",
                border: "2px solid #1C1917",
                boxShadow: "4px 4px 0 #1C1917",
              }}
              data-testid="open-material-library"
            >
              <span
                className="w-9 h-9 flex items-center justify-center shrink-0"
                style={{ background: "#1C1917", color: "#FFB500" }}
              >
                <Layers size={18} strokeWidth={2.5} />
              </span>
              <span className="text-left leading-tight">
                <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--ups-subtext)]">
                  Not sure what to pick?
                </span>
                <span className="block font-display text-sm font-bold">
                  Material Library
                </span>
                <span className="block text-[10px] font-mono opacity-70 mt-0.5">
                  Photos · "feels like" · "best for"
                </span>
              </span>
              <ArrowRight
                size={16}
                className="ml-2 transition-transform group-hover:translate-x-1"
              />
            </a>
          </div>
          <div className="text-xs font-mono" style={{ color: "var(--ups-subtext)" }}>
            Restricted access · whitelisted domains only
          </div>
        </div>
      </div>

      {/* Right: form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 grid-bg">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <h2 className="font-display text-4xl sm:text-5xl font-black leading-none tracking-tight">
              Start a<br />quote session.
            </h2>
            <p className="mt-4 text-sm text-[color:var(--ups-subtext)] leading-relaxed">
              Enter your work email below. If your domain is whitelisted you&apos;ll
              be in instantly with your partner discount applied. New here?
              Request access and we&apos;ll set you up.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="email-gate-form">
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-bold uppercase tracking-[0.18em] mb-2 text-[color:var(--ups-ink)]"
              >
                Work Email
              </label>
              <div className="relative">
                <Mail
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]"
                />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@yourcompany.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError(""); if (accessOpen) setAccessOpen(false); }}
                  className="input-swiss w-full pl-10 pr-4 py-3 text-base rounded-sm"
                  data-testid="email-input"
                  required
                />
              </div>
              {error && (
                <div
                  className="mt-2 text-sm font-medium"
                  style={{ color: "#B91C1C" }}
                  data-testid="email-error"
                >
                  {error}
                </div>
              )}
              <div className="mt-2 text-[11px] font-mono text-[color:var(--ups-subtext)]">
                Whitelisted partner domains only · Gmail, Yahoo &amp; other generic mail providers are not allowed
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-brutal w-full py-3 px-5 rounded-sm flex items-center justify-center gap-2 text-base"
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Checking access…
                </>
              ) : (
                <>
                  Start Quote Session <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Access request prompt — only shows after a non-whitelisted email is entered */}
          {accessOpen && !accessSubmitted && (
            <div
              className="mt-6 p-5 rounded-sm relative"
              style={{ background: "#FFFBEB", border: "2px solid var(--ups-ink)" }}
              data-testid="access-request-panel"
            >
              <button
                type="button"
                onClick={() => setAccessOpen(false)}
                className="absolute top-2 right-2 p-1 rounded hover:bg-white"
                aria-label="Dismiss"
                data-testid="access-request-close"
              >
                <X size={14} />
              </button>
              <div className="overline mb-1">Request Access</div>
              <p className="text-sm text-[color:var(--ups-subtext)] mb-4">
                Tell us a bit about you and we&apos;ll get your domain set up
                with the right discount. We&apos;ll email you back at <span className="font-mono">{email || "your email"}</span>.
              </p>
              <form onSubmit={handleAccessSubmit} className="space-y-3" data-testid="access-request-form">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-1">Your Name</label>
                  <input
                    type="text"
                    value={accessForm.full_name}
                    onChange={(e) => setAccessForm((f) => ({ ...f, full_name: e.target.value }))}
                    className="input-swiss w-full px-3 py-2 rounded-sm text-sm"
                    placeholder="Jane Doe"
                    required
                    data-testid="access-form-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-1">Company (optional)</label>
                  <input
                    type="text"
                    value={accessForm.company}
                    onChange={(e) => setAccessForm((f) => ({ ...f, company: e.target.value }))}
                    className="input-swiss w-full px-3 py-2 rounded-sm text-sm"
                    placeholder="Your company name"
                    data-testid="access-form-company"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.18em] mb-1">What will you print? (optional)</label>
                  <textarea
                    value={accessForm.use_case}
                    onChange={(e) => setAccessForm((f) => ({ ...f, use_case: e.target.value }))}
                    className="input-swiss w-full px-3 py-2 rounded-sm text-sm min-h-[80px]"
                    placeholder="e.g. Bi-weekly flyers + monthly banners for our event team"
                    data-testid="access-form-use-case"
                  />
                </div>
                <button
                  type="submit"
                  disabled={accessSubmitting}
                  className="btn-brutal w-full py-2 px-4 rounded-sm flex items-center justify-center gap-2 text-sm"
                  data-testid="access-form-submit"
                >
                  {accessSubmitting ? (
                    <><Loader2 size={14} className="animate-spin" /> Sending…</>
                  ) : (
                    <>Send Access Request</>
                  )}
                </button>
              </form>
            </div>
          )}
          {accessSubmitted && (
            <div
              className="mt-6 p-5 rounded-sm flex items-start gap-3"
              style={{ background: "#ECFDF5", border: "2px solid #16A34A" }}
              data-testid="access-request-success"
            >
              <CheckCircle2 size={20} className="shrink-0 mt-0.5" style={{ color: "#16A34A" }} />
              <div>
                <div className="text-sm font-bold">Request received.</div>
                <div className="text-sm text-[color:var(--ups-subtext)] mt-1">
                  We&apos;ll email <span className="font-mono">{email}</span> as soon as
                  your domain is set up — usually within one business day.
                </div>
              </div>
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-[color:var(--ups-border)]">
            <div className="overline mb-2">Already a partner?</div>
            <p className="text-sm text-[color:var(--ups-subtext)]">
              The UPS Store internal staff use their <span className="font-mono">@theupsstore.com</span> email.
              Other partner companies sign in with their work email — your discount is applied
              automatically based on your domain.
            </p>
            <a
              href="/admin"
              className="mt-4 inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)] hover:text-[color:var(--ups-ink)]"
              data-testid="admin-link"
            >
              → Admin Panel
            </a>
            {/* Mobile-only catalog launcher — the rich version lives in the left visual panel (desktop). */}
            <button
              type="button"
              onClick={() => setBookOpen(true)}
              className="md:hidden mt-5 w-full inline-flex items-center justify-between gap-3 px-3 py-3 rounded-sm"
              style={{ background: "#1C1917", color: "#FFFBEB", border: "2px solid #1C1917" }}
              data-testid="open-pricing-book-button-mobile"
            >
              <span className="flex items-center gap-2.5">
                <BookOpen size={16} style={{ color: "#FFB500" }} />
                <span className="font-display text-sm font-bold">Print Pricing Book 2026</span>
              </span>
              <ArrowRight size={14} style={{ color: "#FFB500" }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailGate;
